import { Dog } from "../types";

/**
 * "No allergies" / "No health issues" are UI sentinels the quiz stores to mark
 * the question as answered. Records written before they were filtered at the
 * storage boundary still carry them, so strip them on read — otherwise the
 * account page shows an allergy badge reading "NONE_OPTION".
 */
const NONE_SENTINEL = "NONE_OPTION";

function withoutSentinels(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(String).filter((v) => v !== NONE_SENTINEL);
}

/**
 * Normalize a raw dog profile from V1 format to canonical Dog type
 */
function normalizeDog(raw: any, id: string): Dog {
  return {
    // Identity
    id,
    name: raw.name ?? "Unknown",
    breed: raw.breed ?? "Unknown",

    // Demographics (display labels from V1)
    sex: raw.sex ?? undefined,
    reproduction: raw.reproduction ?? undefined,
    ageYears: raw.ageYears ?? undefined,
    ageMonths: raw.ageMonths ?? undefined,

    // Physical attributes
    weightKg: raw.weightKg ?? 10,
    bodyCondition: raw.bodyCondition ?? undefined,
    activityLevel: raw.activityLevel ?? undefined,
    // Dietary & Health
    allergies: withoutSentinels(raw.allergies),
    healthIssues: withoutSentinels(raw.healthIssues),
    treatFrequency: raw.treatFrequency ?? undefined,

    // Nutrition
    dailyGrams: raw.dailyGrams ?? raw.currentNutrition?.dailyGrams ?? undefined,
    dailyCalories:
      raw.dailyCalories ?? raw.currentNutrition?.dailyCalories ?? undefined,
    currentNutrition: raw.currentNutrition ?? undefined,

    // Plan selection
    selectedPackSize: raw.selectedPackSize ?? undefined,
    selectedPlanDays: raw.selectedPlanDays ?? undefined,

    // History
    nutritionHistory: raw.nutritionHistory ?? undefined,

    // Subscription association
    subscription: raw.subscription ?? undefined,

    // Soft delete
    deleted: raw.deleted ?? undefined,
    deletedAt: raw.deletedAt ?? undefined,
  };
}

/**
 * Dogs metafield: custom.paws_dogs
 *
 * Expects V1 format: { version: 1, dogs: [{ id, profile: {...}, orders }] }
 *
 * Legacy formats (object-map, plain array) are no longer supported —
 * run the migration script first.
 */
export function extractDogs(customerJSON: any): Dog[] {
  const c = customerJSON || {};
  let mf = c?.metafields?.paws_dogs;

  // Handle stringified JSON
  if (typeof mf === "string") {
    try {
      mf = JSON.parse(mf);
    } catch {
      return [];
    }
  }

  if (!mf || typeof mf !== "object" || Array.isArray(mf)) {
    if (Array.isArray(mf)) {
      console.warn("[extractDogs] Legacy array format detected — run migration script");
    }
    return [];
  }

  // V1 format
  if (mf.version === 1 && Array.isArray(mf.dogs)) {
    const dogs = mf.dogs
      .filter((entry: any) => entry && typeof entry === "object" && !entry.deleted)
      .map((entry: any) =>
        normalizeDog(entry.profile ?? {}, entry.id ?? "unknown")
      );

    // Deduplicate by id
    const seen = new Set<string>();
    return dogs.filter((dog: Dog) => {
      if (seen.has(dog.id)) return false;
      seen.add(dog.id);
      return true;
    });
  }

  // Legacy object-map format
  console.warn("[extractDogs] Legacy object-map format detected — run migration script");
  return [];
}
